import { Suspense } from 'react'
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { useSubprojectsDataTable } from 'generated/resources/datatable.graphql'
import { ProjectForSelectFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

import { subprojectsProjections } from './projections'

interface SubprojectsDataTableProps {
  showSubprojectsPanel: (newData: ISubprojectsPanelData) => void
  projectId: number
  canManageProjects: boolean
  canManageSubprojects: boolean
  canSeeCosts: boolean
  projectCurrency: string
  hasTimeTrackingSection: boolean
}

const SubprojectsDataTable = ({
  projectId, // TODO: This could be removed and rely on project.id from the context
  showSubprojectsPanel,
  canManageProjects,
  canManageSubprojects,
  canSeeCosts,
  projectCurrency,
  hasTimeTrackingSection,
}: SubprojectsDataTableProps) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { employee } = useCurrentSession()
  const params = useParams()
  const { hasExpenses, hasFlexibleTracking, project } = useOutletContext<{
    hasExpenses: boolean
    hasFlexibleTracking: boolean
    project: ProjectForSelectFragment
  }>()

  const source = useSubprojectsDataTable(
    subprojectsProjections({
      showSubprojectsPanel,
      canSeeCosts,
      currency: projectCurrency,
      canManageSubprojects,
    }),
    {
      variables: {
        projectIds: [projectId],
        name: debouncedSearch,
        includeCost: canSeeCosts,
        includeNoSubproject: true,
      },
    }
  )

  const canExportData =
    canManageProjects &&
    !!employee &&
    (hasTimeTrackingSection || hasExpenses || hasFlexibleTracking)

  return (
    <DataTable.Provider
      source={source}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
        infiniteScroll: false,
      }}
      sort={{
        path: 'name',
      }}
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableExport
          search={{
            value: search,
            onChange: onSearch,
            placeholder: i18n.t('common.search_by'),
          }}
          title={i18n.t('project_management.subprojects.subprojects_tab')}
          customPopoverOptions={
            canExportData && (
              <PopoverMenu.TouchableOption
                title={i18n.t('project_management.table.projects_list.table_options.export.title')}
                description={i18n.t(
                  'project_management.table.projects_list.table_options.export.description'
                )}
                onClick={() => {
                  navigate(
                    navigation.projects.id.details.subprojects.export.fullPath({ id: projectId }),
                    { state: { projectForSelect: project } }
                  )
                }}
              />
            )
          }
          actions={
            canManageSubprojects && (
              <Button.Router
                to={navigation.projects.id.details.subprojects.create.fullPath({
                  id: Number(params.id),
                })}
                onClick={() => {
                  track(trackingEvents.projectManagement.subprojects.addSubprojectStart, {
                    project_id: Number(params.id),
                  })
                }}
                type="secondary"
                iconName="add"
              >
                {i18n.t('project_management.subprojects.add_button')}
              </Button.Router>
            )
          }
        />
        <DataTable.Table autoWidth />
      </Box>
      <Suspense>
        <Outlet context={{ hasExpenses, hasFlexibleTracking }} />
      </Suspense>
    </DataTable.Provider>
  )
}

export default SubprojectsDataTable
